import { useEffect, useMemo, useState } from "react";
import type { PlotLayoutsByBreakpoint } from "@/types/tableState.types";
import type { VivoPlotProps } from "@/components/VivoPlots/types";
import {
  compactLayouts,
  normalizeLayouts,
} from "@/components/Templates/dashboardLayout";

interface UseDashboardLayoutEditingParams {
  visiblePlots: VivoPlotProps[];
  savedLayouts?: PlotLayoutsByBreakpoint;
  persistLayouts: (layouts: PlotLayoutsByBreakpoint) => void;
}

interface DashboardLayoutEditing {
  isLayoutEditMode: boolean;
  activeLayouts: PlotLayoutsByBreakpoint;
  enterLayoutEditMode: (plots?: VivoPlotProps[]) => void;
  cancelLayoutEdit: () => void;
  saveLayout: () => void;
  onLayoutChange: (layouts: PlotLayoutsByBreakpoint) => void;
  compactLayout: () => void;
}

/**
 * Owns the dashboard layout-editing lifecycle: the edit-mode flag, the draft
 * layouts being manipulated, the layouts handed to the grid (static in view
 * mode, draggable/resizable in edit mode), and the beforeunload guard that
 * warns before leaving with an unsaved draft.
 */
export function useDashboardLayoutEditing({
  visiblePlots,
  savedLayouts,
  persistLayouts,
}: UseDashboardLayoutEditingParams): DashboardLayoutEditing {
  const [isLayoutEditMode, setIsLayoutEditMode] = useState(false);
  const [draftLayouts, setDraftLayouts] =
    useState<PlotLayoutsByBreakpoint | null>(null);

  const normalizedLayouts = useMemo(
    () => normalizeLayouts(visiblePlots, savedLayouts),
    [savedLayouts, visiblePlots],
  );

  const activeLayouts = useMemo(() => {
    const source =
      isLayoutEditMode && draftLayouts ? draftLayouts : normalizedLayouts;

    return Object.fromEntries(
      Object.entries(source).map(([breakpoint, items]) => [
        breakpoint,
        (items || []).map((item) => ({
          ...item,
          static: !isLayoutEditMode,
          isDraggable: isLayoutEditMode,
          isResizable: isLayoutEditMode,
        })),
      ]),
    ) as PlotLayoutsByBreakpoint;
  }, [draftLayouts, isLayoutEditMode, normalizedLayouts]);

  const enterLayoutEditMode = (plots: VivoPlotProps[] = visiblePlots) => {
    const layouts = normalizeLayouts(
      plots.filter((plot) => !plot.config.hidden),
      savedLayouts,
    );
    setDraftLayouts(structuredClone(layouts));
    setIsLayoutEditMode(true);
  };

  const compactLayout = () => {
    setDraftLayouts((current) => (current ? compactLayouts(current) : current));
  };

  const cancelLayoutEdit = () => {
    setDraftLayouts(null);
    setIsLayoutEditMode(false);
  };

  const saveLayout = () => {
    if (draftLayouts) persistLayouts(draftLayouts);
    setDraftLayouts(null);
    setIsLayoutEditMode(false);
  };

  useEffect(() => {
    if (!isLayoutEditMode) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isLayoutEditMode]);

  return {
    isLayoutEditMode,
    activeLayouts,
    enterLayoutEditMode,
    cancelLayoutEdit,
    saveLayout,
    onLayoutChange: setDraftLayouts,
    compactLayout,
  };
}
